/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

/**
 * Authentication Context
 * Provides authentication state and methods throughout the app
 * Supports MFA/2FA via Supabase TOTP for admin users
 */
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { User, Session, AuthError, Factor } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase/client';
import { OrganizationWithRole, ROLE_LEVEL } from '@/types/organization';
import { UserProfile, UserType } from '@/types/referral';
import { pickDefaultOrganization } from '@/lib/auth/pickDefaultOrganization';

// MFA related types
type MFAFactorType = 'totp' | 'phone';
interface MFAEnrollTOTPResult {
  id: string;
  type: 'totp';
  totp: {
    qr_code: string;
    secret: string;
    uri: string;
  };
}
interface MFAEnrollPhoneResult {
  id: string;
  type: 'phone';
}
type MFAEnrollResult = MFAEnrollTOTPResult | MFAEnrollPhoneResult;

// Social OAuth provider types
export type AdminOAuthProvider = 'azure' | 'slack' | 'gitlab';
export type CustomerOAuthProvider = 'google' | 'facebook' | 'apple';
export type OAuthProvider = AdminOAuthProvider | CustomerOAuthProvider;
interface AuthContextType {
  user: User | null;
  session: Session | null;
  userProfile: UserProfile | null;
  /**
   * Phase 6 P5.4 — RBAC v2 migration: the legacy `user_profiles.user_type`
   * enum was dropped. `userType` is now a derived label computed from
   * `isAdminUser`:
   *   - `null` while no user is authenticated.
   *   - `'admin'` when the user holds any role assignment (any-org or NULL-
   *     org global admin).
   *   - `'customer'` otherwise.
   * Prefer `isAdminUser` / `isPlatformAdmin` for new code — `userType` is
   * retained as a thin label for the few UI surfaces that branch on a
   * single string.
   */
  userType: UserType | null;
  /**
   * RBAC v2 canonical replacement for the legacy `user_type === 'global_admin'`
   * client-side check. Resolved once per session via
   * `user_has_permission_anywhere(uid, 'users.manage_global_admins')` —
   * org-independent, so it is NOT recomputed on `setCurrentOrganization`.
   * Defaults to `false` until the first probe completes (consumers should
   * gate on `initialising` to avoid showing the false flash). Reset on
   * sign-out.
   */
  isPlatformAdmin: boolean;
  /**
   * Phase 6 P5.3b — RBAC v2 migration. Canonical replacement for the legacy
   * `userType !== 'customer'` check used by session pages and
   * `ProtectedRoute` (`adminOnly` / `customerOnly`).
   *
   * `true` when the user has at least one `user_role_assignments` row in any
   * organisation **or** is a NULL-org global admin. Customers (referral
   * sign-ups) have no role assignments and therefore resolve to `false`.
   *
   * Derived from `organizations.length > 0 || isPlatformAdmin` because
   * `fetchOrganizations` already loads the user's accessible orgs from
   * assignments, and global admins receive the full org list. Reset on
   * sign-out.
   */
  isAdminUser: boolean;
  organizations: OrganizationWithRole[];
  currentOrganization: OrganizationWithRole | null;
  /**
   * True while an auth transition is in flight (sign-in, sign-out, explicit
   * profile refresh). Consumers that just need "have we hydrated the session
   * yet?" should read `initialising` instead so they don't flash a spinner on
   * every token rotation or cross-tab cookie sync.
   */
  loading: boolean;
  /**
   * True only until the very first `supabase.auth.getSession()` resolves.
   * Flipped to false exactly once and never flipped back, even when
   * `loading` toggles for a later sign-in/sign-out. Use this for the
   * "app is still booting" guard in ProtectedRoute-style components.
   */
  initialising: boolean;
  // Auth methods
  signIn: (email: string, password: string, captchaToken?: string, orgId?: string) => Promise<{
    error: AuthError | null;
    mfaRequired?: boolean;
  }>;
  signUp: (email: string, password: string, metadata?: any, captchaToken?: string) => Promise<{
    error: AuthError | null;
  }>;
  signOut: () => Promise<void>;
  signInWithMagicLink: (email: string, redirectTo?: string, metadata?: Record<string, any>) => Promise<{
    error: AuthError | null;
  }>;
  // Social OAuth methods
  signInWithProvider: (provider: OAuthProvider, options?: {
    redirectTo?: string;
    scopes?: string;
    orgSlug?: string;
  }) => Promise<{
    error: AuthError | null;
  }>;
  // MFA methods
  getMFAFactors: () => Promise<{
    factors: Factor[];
    error: AuthError | null;
  }>;
  enrollMFA: (factorType?: MFAFactorType, phone?: string) => Promise<{
    data: MFAEnrollResult | null;
    error: AuthError | null;
  }>;
  enrollPhoneMFA: (phone: string) => Promise<{
    data: MFAEnrollResult | null;
    error: AuthError | null;
  }>;
  verifyMFA: (factorId: string, code: string) => Promise<{
    error: AuthError | null;
  }>;
  unenrollMFA: (factorId: string) => Promise<{
    error: AuthError | null;
  }>;
  challengeMFA: (factorId: string) => Promise<{
    challengeId: string | null;
    error: AuthError | null;
  }>;
  isMFAEnrolled: () => Promise<boolean>;
  getAAL: () => Promise<{
    currentLevel: string | null;
    nextLevel: string | null;
    error: AuthError | null;
  }>;
  /**
   * Returns TRUE iff the current user has any role assignment in `orgId`
   * (or any ancestor org) whose role has `bypass_mfa = TRUE`. Backed by
   * the `public.user_has_mfa_bypass` SQL helper. Returns FALSE when there
   * is no signed-in user or when `orgId` is omitted.
   */
  userHasMfaBypass: (orgId?: string | null) => Promise<boolean>;
  // Organization methods
  setCurrentOrganization: (org: OrganizationWithRole | null) => void;
  refreshOrganizations: () => Promise<void>;
  // Profile methods
  refreshUserProfile: () => Promise<void>;
}

/** Mask a phone number, showing only the last 4 digits */
function maskPhone(phone: string): string {
  if (phone.length <= 4) return phone;
  return '***' + phone.slice(-4);
}

/**
 * Calls the Postgres helper `public.user_has_mfa_bypass(user_uuid, org_uuid)`
 * (Phase 5h.3). Returns TRUE iff the user has any role assignment in `orgId`
 * (or any ancestor org) whose role has `bypass_mfa = TRUE`. Fails closed: any
 * RPC error returns FALSE so a transient DB blip cannot accidentally skip the
 * MFA challenge for a user who would otherwise face it.
 */
async function fetchMfaBypass(userId: string, orgId: string): Promise<boolean> {
  try {
    const {
      data,
      error
    } = await (supabase as any).rpc('user_has_mfa_bypass', {
      user_uuid: userId,
      org_uuid: orgId
    });
    if (error) {
      console.warn('[MFA] user_has_mfa_bypass RPC failed; defaulting to no-bypass:', error.message);
      return false;
    }
    return data === true;
  } catch (err) {
    console.warn('[MFA] user_has_mfa_bypass threw; defaulting to no-bypass:', err);
    return false;
  }
}

/**
 * Calls the Postgres helper `public.user_has_permission_anywhere(uid,
 * 'users.manage_global_admins')`. Returns TRUE iff the user holds the
 * global-admin slug in any org scope they are assigned to. Fails closed:
 * any RPC error returns FALSE so a transient DB blip cannot accidentally
 * grant global-admin UI to a regular org user. Phase 6 P2 (6P2.1) — this
 * is the canonical replacement for the legacy `user_type === 'global_admin'`
 * client-side check; call sites migrated in 6P2.2 → 6P2.4.
 */
export async function fetchPlatformAdminFlag(userId: string): Promise<boolean> {
  try {
    const {
      data,
      error
    } = await (supabase as any).rpc('user_has_permission_anywhere', {
      user_uuid: userId,
      perm_slug: 'users.manage_global_admins'
    });
    if (error) {
      console.warn('[AuthContext] user_has_permission_anywhere RPC failed; defaulting to non-global-admin:', error.message);
      return false;
    }
    return data === true;
  } catch (err) {
    console.warn('[AuthContext] user_has_permission_anywhere threw; defaulting to non-global-admin:', err);
    return false;
  }
}

/**
 * Structural equality for a single organisation row. `fetchOrganizations`
 * re-runs on every `PermissionProvider` revalidation (window focus, tab
 * visibility change and a 30 s interval) and Supabase hands back
 * freshly-deserialised rows each time. Publishing those new object
 * references re-ran every consumer effect keyed on `currentOrganization`,
 * which is what made the agent view look like it randomly reloaded
 * mid-session (#846).
 */
export function isSameOrganization(a: OrganizationWithRole | null, b: OrganizationWithRole | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

/** Structural equality for the organisation list — see isSameOrganization. */
export function isSameOrganizationList(a: OrganizationWithRole[], b: OrganizationWithRole[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  return a.every((org, index) => isSameOrganization(org, b[index]));
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
export function AuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [organizations, setOrganizations] = useState<OrganizationWithRole[]>([]);
  const [currentOrganization, setCurrentOrganization] = useState<OrganizationWithRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [initialising, setInitialising] = useState(true);

  // Deduplication lock: holds the in-flight loadUserData promise so a
  // concurrent caller joins it rather than racing a second fetch.
  const loadUserDataPromiseRef = useRef<Promise<void> | null>(null);

  // Identity-stable publishers for the organisation state. Background
  // revalidations re-fetch the same rows every 30 s; only swap the reference
  // when the payload actually changed so consumer effects stay put (#846).
  const publishOrganizations = (next: OrganizationWithRole[]) => setOrganizations(prev => isSameOrganizationList(prev, next) ? prev : next);
  const publishCurrentOrganization = (next_0: OrganizationWithRole | null) => setCurrentOrganization(prev_0 => isSameOrganization(prev_0, next_0) ? prev_0 : next_0);

  // Identity-stable refetch guard. Tracks the user id whose profile + orgs
  // are currently loaded into context state. `onAuthStateChange` uses this to
  // skip the expensive profile+orgs refetch when Supabase re-fires SIGNED_IN
  // / INITIAL_SESSION on cookie-sync-across-tabs or long-idle tab return —
  // events that don't actually represent an identity change. Without this
  // guard, tabbing away and back triggered two sequential Supabase queries
  // (~300–1500 ms) and a full-screen loading spinner for no reason.
  const lastLoadedUserIdRef = useRef<string | null>(null);

  // Timeout for loadUserData in milliseconds
  const LOAD_USER_DATA_TIMEOUT_MS = 10_000;

  // Fetch (or create) the user profile. Phase 6 P5.4 — RBAC v2: the
  // profile no longer carries the legacy `user_type` enum; admin status
  // is derived elsewhere from `user_role_assignments`.
  const fetchUserProfile = async (userId: string, userMetadata?: Record<string, any>): Promise<void> => {
    try {
      const {
        data,
        error: error_0
      } = await (supabase as any).from('user_profiles').select('*').eq('id', userId).single();
      if (error_0 && error_0.code !== 'PGRST116') {
        // PGRST116 = no rows returned
        throw error_0;
      }
      if (data) {
        if (data.status === 'inactive') {
          supabase.auth.signOut().catch(e => console.warn('signOut error:', e));
          setUserProfile(null);
          return;
        }
        setUserProfile(data);
        return;
      }

      // Create a default profile if one doesn't yet exist. Admin/customer
      // status is no longer stored here — it follows from role assignments.
      const isReferralUser = !!userMetadata?.organization_slug;
      const {
        data: newProfile,
        error: createError
      } = await (supabase as any).from('user_profiles').insert({
        id: userId,
        metadata: isReferralUser ? {
          organization_slug: userMetadata.organization_slug
        } : {}
      }).select().single();
      if (createError) throw createError;
      setUserProfile(newProfile);
    } catch (error) {
      console.warn('Error fetching user profile:', error);
      setUserProfile(null);
    }
  };

  // Fetch user's organizations (including child orgs via parent membership).
  // Phase 6 P5.4 — RBAC v2: global-admin status is now passed in
  // explicitly (resolved upstream via `user_has_permission_anywhere`)
  // rather than derived from the legacy `user_type === 'global_admin'`
  // enum. Global admins receive the full org list with synthetic owner
  // role, mirroring the previous behaviour.
  const fetchOrganizations = async (userId_0: string, platformAdmin: boolean) => {
    try {
      if (platformAdmin) {
        // Global admins can see all organisations
        const {
          data: allOrgs,
          error: allOrgsError
        } = await (supabase as any).from('organizations').select('*').order('name');
        if (allOrgsError) throw allOrgsError;

        // Give global admins 'owner' role on all orgs
        const orgs: OrganizationWithRole[] = (allOrgs || []).map((org: any) => ({
          ...org,
          access_type: 'global_admin',
          role_slug: 'owner' as const,
          role_level: ROLE_LEVEL.owner
        }));
        publishOrganizations(orgs);

        // Handle current org selection
        const savedOrgId = localStorage.getItem('currentOrganizationId');
        if (savedOrgId) {
          const savedOrg = orgs.find(o => o.id === savedOrgId);
          if (savedOrg) {
            publishCurrentOrganization(savedOrg);
            return;
          }
          localStorage.removeItem('currentOrganizationId');
        }
        if (orgs.length > 0) {
          publishCurrentOrganization(orgs[0]);
          localStorage.setItem('currentOrganizationId', orgs[0].id);
        }
        return;
      }

      // Use the new function that includes hierarchical access
      const {
        data: data_0,
        error: error_2
      } = await (supabase.rpc as any)('get_user_accessible_organizations', {
        user_uuid: userId_0
      });
      if (error_2) {
        // Fallback to the old function if new one doesn't exist
        console.warn('get_user_accessible_organizations not available, falling back to get_user_organizations');
        const {
          data: fallbackData,
          error: fallbackError
        } = await (supabase.rpc as any)('get_user_organizations', {
          user_uuid: userId_0
        });
        if (fallbackError) throw fallbackError;
        const orgIds = fallbackData.map((row: any) => row.organization_id);
        const {
          data: fullOrgs,
          error: orgsError
        } = await (supabase as any).from('organizations').select('*').in('id', orgIds);
        if (orgsError) throw orgsError;
        const orgs_0: OrganizationWithRole[] = fallbackData.map((row_0: any) => {
          const fullOrg = fullOrgs.find((org_0: any) => org_0.id === row_0.organization_id);
          return {
            ...fullOrg,
            role_slug: row_0.role_slug,
            role_level: row_0.role_level
          };
        });
        publishOrganizations(orgs_0);

        // Restore saved org preference or default to first
        const savedOrgId_0 = localStorage.getItem('currentOrganizationId');
        if (savedOrgId_0) {
          const savedOrg_0 = orgs_0.find(o_0 => o_0.id === savedOrgId_0);
          if (savedOrg_0) {
            publishCurrentOrganization(savedOrg_0);
            return;
          }
          localStorage.removeItem('currentOrganizationId');
        }

        // Wave 5 (E1) — pick the highest-ancestor org with direct
        // membership rather than the first row returned. For global
        // admins (`access_type === 'global_admin'`) every org is
        // synthesised; the picker still prefers a root parent over a
        // child org so the dashboard scope is consistent across logins.
        const defaultOrg = pickDefaultOrganization(orgs_0);
        if (defaultOrg) {
          publishCurrentOrganization(defaultOrg);
          localStorage.setItem('currentOrganizationId', defaultOrg.id);
        }
        return;
      }

      // Fetch full organization details for each org
      const orgIds_0 = data_0.map((row_1: any) => row_1.organization_id);
      const {
        data: fullOrgs_0,
        error: orgsError_0
      } = await (supabase as any).from('organizations').select('*').in('id', orgIds_0);
      if (orgsError_0) throw orgsError_0;

      // Merge access type + v2 role slug/level with full organization data
      const orgs_1: OrganizationWithRole[] = data_0.map((row_2: any) => {
        const fullOrg_0 = fullOrgs_0.find((org_1: any) => org_1.id === row_2.organization_id);
        return {
          ...fullOrg_0,
          access_type: row_2.access_type,
          // 'direct' or 'inherited'
          role_slug: row_2.role_slug,
          role_level: row_2.role_level
        };
      });
      publishOrganizations(orgs_1);

      // Check localStorage for saved organization preference FIRST
      const savedOrgId_1 = localStorage.getItem('currentOrganizationId');
      if (savedOrgId_1) {
        // Try to restore saved organization
        const savedOrg_1 = orgs_1.find(o_1 => o_1.id === savedOrgId_1);
        if (savedOrg_1) {
          publishCurrentOrganization(savedOrg_1);
          return; // Exit early - we've restored the saved preference
        }
        // Saved org not found in list (maybe user lost access), clear it
        localStorage.removeItem('currentOrganizationId');
      }

      // Update current organization if it exists in the new list
      if (currentOrganization) {
        const updatedCurrentOrg = orgs_1.find(o_2 => o_2.id === currentOrganization.id);
        if (updatedCurrentOrg) {
          publishCurrentOrganization(updatedCurrentOrg);
          return;
        }
      }

      // Set the default landing org. Wave 5 (E1): replaces the previous
      // `orgs[0]` fallback so users with both direct (parent) and
      // inherited (child) memberships — Coordinator being the canonical
      // example — land on the parent rather than an arbitrary child.
      const defaultOrg_0 = pickDefaultOrganization(orgs_1);
      if (defaultOrg_0) {
        publishCurrentOrganization(defaultOrg_0);
        localStorage.setItem('currentOrganizationId', defaultOrg_0.id);
      }
    } catch (error_1) {
      // Leave the previously-resolved list in place. This runs on every
      // background revalidation (window focus, 30 s interval), and wiping
      // the orgs on a transient network blip flipped `isAdminUser` to false
      // for a render, flashing "Access Denied" / "No Organization
      // Selected" over a perfectly good session (#846).
      console.warn('Error fetching organisations:', error_1);
    }
  };

  // Initialize auth state
  useEffect(() => {
    // Helper to load user data sequentially - profile first, then orgs with userType.
    // Includes a 10-second timeout so the app never hangs.
    const runLoadUserData = async (userId_1: string, userMetadata_0?: Record<string, any>) => {
      // Guard consumers (ProtectedRoute) against rendering a partial state
      // while profile + organisations are still being fetched.
      setLoading(true);
      try {
        const dataPromise = (async () => {
          await fetchUserProfile(userId_1, userMetadata_0);
          // Resolve the global-admin flag before fetching organisations
          // so `fetchOrganizations` can decide between "all orgs" (platform
          // admin) and "only assigned orgs" without re-querying the
          // permission predicate. The answer is org-independent (any-org
          // probe), so we deliberately do NOT recompute on
          // `setCurrentOrganization`. Failure mode is closed (false) — see
          // fetchPlatformAdminFlag.
          const platformAdmin_0 = await fetchPlatformAdminFlag(userId_1);
          setIsPlatformAdmin(platformAdmin_0);
          await fetchOrganizations(userId_1, platformAdmin_0);
        })();
        const timeoutPromise = new Promise<'timeout'>(resolve => setTimeout(() => resolve('timeout'), LOAD_USER_DATA_TIMEOUT_MS));
        const result = await Promise.race([dataPromise, timeoutPromise]);
        if (result === 'timeout') {
          console.warn('[AuthContext] loadUserData timed out after 10 s — using stale data');
        }
      } catch (error_3) {
        console.warn('[AuthContext] loadUserData failed:', error_3);
      } finally {
        setLoading(false);
      }
    };

    // Deduplication guard. A duplicate caller JOINS the in-flight load
    // rather than returning immediately: the initial `getSession()` path
    // awaits this before flipping `initialising` to false, and returning
    // early let the boot window close while profile + organisations were
    // still loading — the render gap that flashed "Access Denied" on a
    // manual page refresh (#846).
    const loadUserData = async (userId_2: string, userMetadata_1?: Record<string, any>) => {
      if (loadUserDataPromiseRef.current) {
        console.warn('[AuthContext] loadUserData already in progress, joining in-flight call');
        await loadUserDataPromiseRef.current;
        return;
      }
      const inFlight = runLoadUserData(userId_2, userMetadata_1);
      loadUserDataPromiseRef.current = inFlight;
      try {
        await inFlight;
      } finally {
        loadUserDataPromiseRef.current = null;
      }
    };

    // Get initial session. `initialising` is flipped to false exactly once
    // after this resolves, regardless of whether a session was found.
    //
    // We must also handle the case where `getSession()` REJECTS — gotrue-js
    // serialises every `supabase.auth.*` call through a single Web-Lock and
    // a competing call (e.g. `setSession()` on /auth/accept-invite or
    // /auth/callback-client mounted under the same provider) can steal the
    // lock and cause this promise to reject with "released because another
    // request stole it". Without the catch, `initialising` would stay true
    // forever and every consumer would hang on a spinner. The competing
    // operation will publish the resolved session via `onAuthStateChange`
    // moments later, so swallowing the rejection is safe.
    supabase.auth.getSession().then(async ({
      data: {
        session: session_0
      }
    }) => {
      setSession(session_0);
      setUser(session_0?.user ?? null);
      if (session_0?.user) {
        lastLoadedUserIdRef.current = session_0.user.id;
        await loadUserData(session_0.user.id, session_0.user.user_metadata);
      } else {
        setLoading(false);
      }
    }).catch(err => {
      console.warn('[AuthContext] initial getSession() rejected; deferring to onAuthStateChange:', err);
      setLoading(false);
    }).finally(() => {
      setInitialising(false);
    });

    // Listen for auth changes
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange(async (event, session_1) => {
      // Session must stay fresh so downstream API calls use the current JWT,
      // but Supabase re-fires SIGNED_IN / INITIAL_SESSION on cross-tab cookie
      // sync and tab return carrying the *same* token. Publishing a new
      // object reference for an unchanged session re-rendered every
      // useAuth() consumer for nothing, so only swap when the token or the
      // identity actually moved (#846).
      setSession(prev_1 => prev_1?.access_token === session_1?.access_token && prev_1?.user?.id === session_1?.user?.id ? prev_1 : session_1);

      // Identity-stable user updates. Supabase hands us a new `session.user`
      // object on every event (including TOKEN_REFRESHED on tab return) even
      // when the user's identity has not changed. Re-publishing a fresh
      // reference causes every useAuth() consumer to re-render and any
      // useEffect keyed on `user` to re-run — which is what made the app
      // feel like it was "refreshing" on tab switch. We therefore only swap
      // the reference when the id actually changes, EXCEPT for USER_UPDATED
      // (which is the single event that can carry changed metadata for the
      // same id — e.g. email/phone/MFA updates).
      if (event === 'USER_UPDATED') {
        setUser(session_1?.user ?? null);
      } else {
        setUser(prev_2 => prev_2?.id === session_1?.user?.id ? prev_2 : session_1?.user ?? null);
      }

      // TOKEN_REFRESHED fires every time Supabase silently rotates the JWT —
      // most commonly when the tab regains focus after the user switched away
      // and came back. USER_UPDATED fires on auth metadata mutations. Neither
      // changes identity or org membership, so a full profile+orgs refetch is
      // unnecessary. Flipping loading on these used to flash the global
      // loading screen (looking like an auto-reload) and, on slow networks,
      // trip the 10s guard in loadUserData.
      if (event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
        return;
      }
      if (session_1?.user) {
        // Identity-stable refetch. Supabase can re-fire SIGNED_IN /
        // INITIAL_SESSION on cross-tab cookie sync or long-idle tab return
        // with the same user. Re-running loadUserData in that case is what
        // produced the "tabbed off, came back to a full-page reload" feel.
        // Only refetch when the id actually changes (real sign-in, account
        // switch) or when we lost context state (loadUserData failed).
        if (lastLoadedUserIdRef.current === session_1.user.id) {
          return;
        }
        lastLoadedUserIdRef.current = session_1.user.id;
        // Flip loading on BEFORE awaiting loadUserData so downstream consumers
        // (e.g. ProtectedRoute) don't see a window where `user` is truthy but
        // `userType`/`currentOrganization` are still null. Without this,
        // post-login renders briefly show the "No Organization Selected" or
        // "Access Denied" screens and only recover on a fresh tab.
        setLoading(true);
        await loadUserData(session_1.user.id, session_1.user.user_metadata);
      } else {
        lastLoadedUserIdRef.current = null;
        setUserProfile(null);
        setIsPlatformAdmin(false);
        setOrganizations([]);
        setCurrentOrganization(null);
        localStorage.removeItem('currentOrganizationId');
        setLoading(false);
      }
    });
    return () => subscription.unsubscribe();
  }, []);
  const signIn = async (email: string, password: string, captchaToken?: string, orgId?: string) => {
    const {
      data: signInData,
      error: error_4
    } = await supabase.auth.signInWithPassword({
      email,
      password,
      options: captchaToken ? {
        captchaToken
      } : undefined
    });
    if (error_4) return {
      error: error_4
    };

    // Check if MFA is required (AAL1 means first factor complete, need AAL2)
    const {
      data: aalData
    } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (aalData && aalData.nextLevel === 'aal2' && aalData.currentLevel === 'aal1') {
      // RBAC v2 bypass — only honoured when the caller passed an orgId (i.e.
      // the branded /admin/login/{slug} page knows which org the user is
      // entering). The generic /admin/login page does NOT pass an orgId, so
      // the legacy AAL2 challenge stands. Spec §8.8.
      const userId_3 = signInData?.user?.id;
      if (userId_3 && orgId) {
        const bypass = await fetchMfaBypass(userId_3, orgId);
        if (bypass) {
          return {
            error: null,
            mfaRequired: false
          };
        }
      }
      return {
        error: null,
        mfaRequired: true
      };
    }
    return {
      error: null
    };
  };
  const userHasMfaBypass = async (orgId_0?: string | null): Promise<boolean> => {
    if (!user || !orgId_0) return false;
    return fetchMfaBypass(user.id, orgId_0);
  };
  const signUp = async (email_0: string, password_0: string, metadata?: any, captchaToken_0?: string) => {
    const {
      error: error_5
    } = await supabase.auth.signUp({
      email: email_0,
      password: password_0,
      options: {
        data: metadata,
        ...(captchaToken_0 ? {
          captchaToken: captchaToken_0
        } : {})
      }
    });
    return {
      error: error_5
    };
  };
  const signOut = async () => {
    await supabase.auth.signOut();
    setOrganizations([]);
    setCurrentOrganization(null);
    setIsPlatformAdmin(false);
    localStorage.removeItem('currentOrganizationId');
  };

  // Magic link sign in for end users
  const signInWithMagicLink = async (email_1: string, redirectTo?: string, metadata_0?: Record<string, any>) => {
    const {
      error: error_6
    } = await supabase.auth.signInWithOtp({
      email: email_1,
      options: {
        emailRedirectTo: redirectTo || `${window.location.origin}/customer/dashboard`,
        data: metadata_0
      }
    });
    return {
      error: error_6
    };
  };

  // Social OAuth sign in
  const signInWithProvider = async (provider: OAuthProvider, options?: {
    redirectTo?: string;
    scopes?: string;
    orgSlug?: string;
  }) => {
    // Build redirect URL with optional org slug
    let redirectTo_0 = options?.redirectTo || `${window.location.origin}/auth/callback`;

    // Add org slug to redirect URL if provided (for customer signup)
    if (options?.orgSlug) {
      const url = new URL(redirectTo_0);
      url.searchParams.set('org_slug', options.orgSlug);
      redirectTo_0 = url.toString();
    }
    const {
      error: error_7
    } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: redirectTo_0,
        scopes: options?.scopes
      }
    });
    return {
      error: error_7
    };
  };

  // MFA Methods
  const getMFAFactors = async () => {
    const {
      data: data_1,
      error: error_8
    } = await supabase.auth.mfa.listFactors();
    // `data.totp`/`data.phone` only contain *verified* factors; `data.all`
    // also includes unverified ones left behind by an abandoned enrolment.
    // Return both (still scoped to totp/phone) so callers can detect verified
    // factors and clean up orphaned, unverified ones that would otherwise
    // block re-enrolment via a friendly-name conflict.
    const allFactors = (data_1?.all ?? []).filter(f => f.factor_type === 'totp' || f.factor_type === 'phone');
    return {
      factors: allFactors,
      error: error_8
    };
  };
  const enrollMFA = async (factorType: MFAFactorType = 'totp', phone?: string) => {
    if (factorType === 'phone') {
      if (!phone) {
        return {
          data: null,
          error: {
            message: 'Phone number is required for SMS enrolment',
            status: 400
          } as any as AuthError
        };
      }
      const {
        data: data_2,
        error: error_9
      } = await supabase.auth.mfa.enroll({
        factorType: 'phone',
        phone,
        friendlyName: `SMS (${maskPhone(phone)})`
      } as any);
      if (error_9 || !data_2) {
        return {
          data: null,
          error: error_9
        };
      }
      return {
        data: {
          id: data_2.id,
          type: 'phone' as const
        },
        error: null
      };
    }

    // TOTP enrolment
    const {
      data: data_3,
      error: error_10
    } = await supabase.auth.mfa.enroll({
      factorType: 'totp',
      friendlyName: 'Authenticator App'
    });
    if (error_10 || !data_3) {
      return {
        data: null,
        error: error_10
      };
    }
    return {
      data: {
        id: data_3.id,
        type: 'totp' as const,
        totp: {
          qr_code: data_3.totp.qr_code,
          secret: data_3.totp.secret,
          uri: data_3.totp.uri
        }
      },
      error: null
    };
  };
  const enrollPhoneMFA = async (phone_0: string) => {
    return enrollMFA('phone', phone_0);
  };
  const challengeMFA = async (factorId: string) => {
    const {
      data: data_4,
      error: error_11
    } = await supabase.auth.mfa.challenge({
      factorId
    });
    return {
      challengeId: data_4?.id || null,
      error: error_11
    };
  };
  const verifyMFA = async (factorId_0: string, code: string) => {
    // First create a challenge
    const {
      data: challengeData,
      error: challengeError
    } = await supabase.auth.mfa.challenge({
      factorId: factorId_0
    });
    if (challengeError) {
      console.error('[MFA] Challenge creation failed:', challengeError);
      return {
        error: challengeError
      };
    }

    // Then verify with the code
    const {
      error: error_12
    } = await supabase.auth.mfa.verify({
      factorId: factorId_0,
      challengeId: challengeData.id,
      code
    });
    if (error_12) {
      console.error('[MFA] Verification failed:', error_12);
    }
    return {
      error: error_12
    };
  };
  const unenrollMFA = async (factorId_1: string) => {
    const {
      error: error_13
    } = await supabase.auth.mfa.unenroll({
      factorId: factorId_1
    });
    return {
      error: error_13
    };
  };
  const isMFAEnrolled = async () => {
    const {
      data: data_5
    } = await supabase.auth.mfa.listFactors();
    const totpCount = data_5?.totp?.length || 0;
    const phoneCount = data_5?.phone?.length || 0;
    return totpCount + phoneCount > 0;
  };
  const getAAL = async () => {
    const {
      data: data_6,
      error: error_14
    } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    return {
      currentLevel: data_6?.currentLevel || null,
      nextLevel: data_6?.nextLevel || null,
      error: error_14
    };
  };
  const handleSetCurrentOrganization = (org_2: OrganizationWithRole | null) => {
    setCurrentOrganization(org_2);
    if (org_2) {
      localStorage.setItem('currentOrganizationId', org_2.id);
    } else {
      localStorage.removeItem('currentOrganizationId');
    }
  };
  const refreshOrganizations = async () => {
    if (user) {
      await fetchOrganizations(user.id, isPlatformAdmin);
    }
  };
  const refreshUserProfile = async () => {
    if (user) {
      await fetchUserProfile(user.id, user.user_metadata);
    }
  };

  // Phase 6 P5.3b — RBAC v2: `isAdminUser` is true when the user holds any
  // role assignment (org membership) or is a global admin. Customers have
  // no `user_role_assignments` rows, so `fetchOrganizations` returns an empty
  // list and this flag resolves to `false`.
  const isAdminUser = isPlatformAdmin || organizations.length > 0;

  // Phase 6 P5.4 — RBAC v2: `userType` is now a derived label (the legacy
  // `user_profiles.user_type` enum was dropped). Resolves to `null` until
  // a user is authenticated, then to `'admin'` for any role-holding user
  // and `'customer'` otherwise.
  const userType: UserType | null = !user ? null : isAdminUser ? 'admin' : 'customer';
  const value = {
    user,
    session,
    userProfile,
    userType,
    isPlatformAdmin,
    isAdminUser,
    organizations,
    currentOrganization,
    loading,
    initialising,
    // Auth methods
    signIn,
    signUp,
    signOut,
    signInWithMagicLink,
    signInWithProvider,
    // MFA methods
    getMFAFactors,
    enrollMFA,
    enrollPhoneMFA,
    verifyMFA,
    unenrollMFA,
    challengeMFA,
    isMFAEnrolled,
    getAAL,
    userHasMfaBypass,
    // Organization methods
    setCurrentOrganization: handleSetCurrentOrganization,
    refreshOrganizations,
    // Profile methods
    refreshUserProfile
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}